/**
 * Matrice des **forfaits de transfert** : une ligne par liaison, une colonne
 * par classe de véhicule.
 *
 * Le hub /chauffeur/transferts n'affichait qu'un prix par liaison (le tarif
 * d'entrée, Class E) sur ses cartes. La grille par classe existe pourtant déjà
 * dans les données (`VEHICLES[].fixedTransfers`) et est servie sur chaque page
 * trajet : la republier ici en tableau répond directement à « combien coûte un
 * transfert aéroport de Nice → Saint-Tropez en Classe S », et donne au hub le
 * contenu factuel qui lui manquait (767 mots, la page la plus maigre du silo
 * alors qu'elle en est la tête).
 *
 * Données via `getRoutePrices()` — donc exactement les mêmes classes et prix
 * que les pages trajet, y compris la décision de ne pas exposer le véhicule
 * blindé (`VEHICLE_DISPLAY_ORDER` dans data/transfers). Aucun montant en dur.
 *
 * Composant serveur. i18n FR/EN/AR, classes logiques (RTL-safe).
 */
import Link from "next/link";
import {
  TRANSFER_ROUTES,
  getRoutePrices,
  getRouteLabelBidirectional,
  localizedRouteContent,
} from "@/data/transfers";
import type { Locale } from "@/config/i18n";
import { tr, type I18n } from "@/config/localeText";
import { formatEuro } from "./formatPrice";

type Dict = {
  caption: string;
  route: string;
  note: string;
};

const UI: I18n<Dict> = {
  fr: {
    caption:
      "Forfaits de transfert par liaison et par classe de véhicule, toutes taxes comprises",
    route: "Liaison",
    note: "Forfaits fixés à l'avance, valables dans les deux sens, taxes et péages inclus. Le suivi de vol est systématique sur les transferts aéroport. Toute liaison hors de cette grille est estimée au kilomètre.",
  },
  en: {
    caption:
      "Fixed transfer prices by route and vehicle class, all taxes included",
    route: "Route",
    note: "Prices fixed in advance, valid in both directions, taxes and tolls included. Flight tracking is standard on airport transfers. Any route outside this grid is quoted per kilometre.",
  },
  ar: {
    caption: "أسعار النقل الثابتة حسب الوجهة وفئة السيارة، شاملة الضرائب",
    route: "الوجهة",
    note: "أسعار محدَّدة مسبقًا وصالحة في الاتجاهين، شاملة الضرائب ورسوم الطرق. تتبّع الرحلات الجوية قياسي في نقل المطار. أي وجهة خارج هذا الجدول تُسعَّر بالكيلومتر.",
  },
};

export function TransferRatesTable({
  locale = "fr",
}: {
  locale?: Locale;
}): JSX.Element {
  const t = tr(UI, locale);

  // Colonnes = classes réellement tarifées, lues sur la 1re liaison (la grille
  // est homogène : toutes les liaisons couvrent les mêmes classes).
  const columns = getRoutePrices(TRANSFER_ROUTES[0]);

  return (
    <div>
      <div className="overflow-x-auto rounded-2xl border border-black/10 bg-white">
        <table className="w-full min-w-[40rem] border-collapse text-start text-sm">
          <caption className="sr-only">{t.caption}</caption>
          <thead>
            <tr className="border-b border-black/10 bg-black/[0.03] text-xs uppercase tracking-[0.12em] text-black/60">
              <th scope="col" className="px-4 py-3 text-start font-semibold">
                {t.route}
              </th>
              {columns.map((column) => (
                <th
                  key={column.vehicleId}
                  scope="col"
                  className="whitespace-nowrap px-3 py-3 text-end font-semibold"
                >
                  {column.classLabel}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TRANSFER_ROUTES.map((route) => {
              const prices = getRoutePrices(route);
              // `seo.canonical` porte déjà le chemin localisé complet dans les
              // trois registres (routes.ts / routesEn.ts / routesAr.ts), y
              // compris les slugs EN qui diffèrent du FR.
              const href = localizedRouteContent(route, locale).seo.canonical;
              return (
                <tr
                  key={route.slug}
                  className="border-b border-black/5 last:border-0"
                >
                  <th scope="row" className="px-4 py-3 text-start font-medium">
                    <Link
                      href={href}
                      className="text-black/90 underline-offset-4 transition-colors hover:text-primary hover:underline"
                    >
                      {getRouteLabelBidirectional(route, locale)}
                    </Link>
                  </th>
                  {columns.map((column) => {
                    const cell = prices.find(
                      (p) => p.vehicleId === column.vehicleId,
                    );
                    return (
                      <td
                        key={column.vehicleId}
                        className="whitespace-nowrap px-3 py-3 text-end font-semibold text-black"
                      >
                        {cell ? formatEuro(cell.price) : "—"}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-black/65">{t.note}</p>
    </div>
  );
}

export default TransferRatesTable;
